<template>
    <div>
        <h1 class="text-h5 my-5">
            <router-link :to="{name: 'overview', query: $api.contextQuery()}">Applications</router-link> / {{$utils.appId(id).name}}
            <v-progress-circular v-if="loading" indeterminate color="green" class="ml-3"/>
        </h1>

        <div v-if="app">
            <AppMap v-if="app.app_map" :map="app.app_map" class="my-5" />

            <v-tabs v-if="app.reports && app.reports.length" height="40" show-arrows slider-size="2">
                <v-tab v-for="r in app.reports" :key="r.name" :to="{params: {report: r.name}, query: $api.contextQuery()}" exact-path>
                    <Led v-if="r && (r.checks || r.configuration_hint)" :status="r.status" />
                    {{r.name}}
                </v-tab>
            </v-tabs>

            <v-card v-if="r && r.checks && !r.custom" outlined class="my-4 pa-4 pb-2">
                <CheckDetails v-for="check in r.checks" :check="check" @configure="configureInspection(check)" class="mb-2" />
            </v-card>

            <v-alert v-if="r && r.configuration_hint" color="info" outlined text class="my-4">
                {{ r.configuration_hint.message }}
                <template v-if="r.configuration_hint.read_more_link">
                    (<a :href="r.configuration_hint.read_more_link" target="_blank">Read more <v-icon small>mdi-open-in-new</v-icon></a>)
                </template>
            </v-alert>

            <template v-if="r">
                <div v-if="r.name === 'SLO'" class="d-flex flex-wrap">
                    <div v-for="(w, i) in r.widgets" :key="r.name+':'+i" class="my-5" :style="{width: $vuetify.breakpoint.mdAndUp ? (w.width || '50%') : '100%'}">
                        <Chart v-if="w.chart" :chart="w.chart" @select="chartSelect" :selection="{}" />
                        <Widget v-else :w="w" />
                    </div>
                </div>
                <Logs v-else-if="r.name === 'Logs'" :appId="id" :check="r.widgets[0].logs.check" class="my-5">
                    <template #check>
                        <CheckDetails :check="r.widgets[0].logs.check" @configure="configureInspection(r.widgets[0].logs.check)" />
                    </template>
                </Logs>
                <RCA v-else-if="r.name === 'RCA'" :appId="id" class="my-5" />
                <Dashboard v-else :name="r.name" :widgets="r.widgets" />
            </template>

            <InspectionForm v-model="form.open" :inspection_id="form.inspection_id" :project_id="projectId" :application_id="id" />
        </div>
        <NoData v-else-if="!loading" />
    </div>
</template>

<script>
import NoData from "@coroot/coroot/src/components/NoData.vue";
import AppMap from "@coroot/coroot/src/components/AppMap.vue";
import Led from "@coroot/coroot/src/components/Led.vue";
import Dashboard from "@coroot/coroot/src/components/Dashboard.vue";
import Widget from "@coroot/coroot/src/components/Widget.vue";
import Chart from "@coroot/coroot/src/components/Chart.vue";
import CheckDetails from "@coroot/coroot/src/components/CheckDetails.vue";
import InspectionForm from "@/pages/console/InspectionForm.vue";
import Logs from "@coroot/coroot/src/views/Logs.vue";
import RCA from "@/pages/console/RCA.vue";

export default {
    props: {
        workspaceId: String,
        projectId: String,
        id: String,
        report: String,
    },

    components: {NoData, AppMap, Led, Dashboard, InspectionForm, Logs, RCA, Widget, Chart, CheckDetails},

    data() {
        return {
            app: null,
            loading: false,
            r: null,
            form: {
                open: false,
                inspection_id: null,
            }
        };
    },

    watch: {
        id() {
            this.app = null;
            this.get();
        },
        report() {
            this.showReport();
        },
        'form.open'(v) {
            !v && this.get();
        }
    },

    mounted() {
        this.get();
        this.$events.watch(this, this.get, 'refresh');
    },

    methods: {
        chartSelect(s) {
            const query = {...this.$route.query, rcaFrom: s.selection.from, rcaTo: s.selection.to, ...s.ctx};
            this.$router.push({params: {report: 'RCA'}, query}).catch(err => err);
        },
        configureInspection(inspection) {
            this.form.open = true;
            this.form.inspection_id = inspection.id;
        },
        condition(c) {
            const parts = c.condition_format_template.split('<threshold>', 2);
            if (parts.length === 0) {
                return {head: '', tail: ''};
            }
            if (parts.length === 1) {
                return {head: parts[0], tail: ''};
            }
            return {head: parts[0], tail: parts[1]};
        },
        threshold(c) {
            if (c.unit === 'second') {
                return c.threshold < 1 ? c.threshold*1000+'ms' : c.threshold+'s';
            }
            if (c.unit === 'percent') {
                return c.threshold+'%';
            }
            return c.threshold+c.unit;
        },
        get() {
            this.loading = true;
            this.$api.getApplication(this.id, (data, meta) => {
                this.loading = false;
                this.app = data;
                this.showReport();
            });
        },
        showReport() {
            if (!this.app || !this.app.reports || !this.app.reports.length) {
                this.r = null;
                return;
            }
            if (!this.report) {
                if (this.app.reports.length > 1 && this.app.reports[0].name === 'SLO' &&  this.app.reports[0].status === 'unknown' ) {
                    this.r = this.app.reports[1];
                } else {
                    this.r = this.app.reports[0];
                }
                this.$router.replace({params: {report: this.r.name}, query: this.$api.contextQuery()}).catch(err => err);
                return;
            }
            const r = this.app.reports.find((r) => r.name === this.report);
            if (!r) {
                this.$router.replace({params: {report: null}, query: this.$api.contextQuery()}).catch(err => err);
                return;
            }
            this.r = r;
        },
    },
};
</script>
